import React, { useCallback, ReactNode, FC } from 'react'
import { SxProps } from '@mui/system'
import Dialog, { DialogProps } from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import TinyButton from '../widgets/TinyButton'

interface WindowProps {
  leftButtons?: ReactNode,
  rightButtons?: ReactNode,
  buttons?: ReactNode,
  smallButtons?: boolean,
  withCancel?: boolean,
  withSubmit?: boolean,
  loading?: boolean,
  submitTitle?: string,
  cancelTitle?: string,
  open: boolean,
  title?: string | ReactNode,
  size?: DialogProps["maxWidth"],
  compact?: boolean,
  noScroll?: boolean,
  fullHeight?: boolean,
  noActions?: boolean,
  onCancel?: {
    (): void,
  },
  onSubmit?: {
    (): void,
  },
  theme?: Record<string, string>,
  disabled?: boolean,
  sx?: SxProps,
}

const Window: FC<React.PropsWithChildren<WindowProps>> = ({
  leftButtons,
  rightButtons,
  buttons,
  smallButtons = false,
  withCancel,
  withSubmit = true,
  loading = false,
  submitTitle = 'Save',
  cancelTitle = 'Cancel',
  open,
  title,
  size = 'md',
  children,
  compact = false,
  noScroll = false,
  fullHeight = false,
  noActions = false,
  onCancel,
  onSubmit,
  theme = {},
  sx = {},
  disabled = false,
}) => {

  const ButtonComponent = smallButtons ?
    TinyButton :
    Button

  const closeWindow = useCallback(() => {
    onCancel && onCancel()
  }, [
    onCancel,
  ])

  return (
    <Dialog
      open={ open }
      onClose={ closeWindow }
      fullWidth
      maxWidth={ size }
      scroll="paper"
      PaperProps={{
        sx: {
          height: fullHeight ? '90%' : 'auto',
          maxHeight: fullHeight ? '90%' : 'auto',
          ...sx,
        },
      }}
      sx={{
        padding: '0px!important' as any,
      }}
    >
      {
        title && (
          <DialogTitle
            sx={{
              pt: 2,
              pl: 3,
              pb: 2,
              pr: 3,
            }}
          >
            <Typography variant="h5" color="primary" component="span">
              { title }
            </Typography>
          </DialogTitle>
        )
      }
      <DialogContent>
        { children }
      </DialogContent>
      {
        !noActions && (
          <DialogActions>
            <Box
              className="Buttons Container"
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <Box
                className="Buttons Left"
                sx={{
                  flexGrow: 0,
                }}
              >
                { leftButtons }
              </Box>
              <Box
                className="Buttons Right"
                sx={{
                  flexGrow: 1,
                  textAlign: 'right',
                }}
              >
                {
                  withCancel && (
                    <ButtonComponent
                      sx={{
                        ml: 2,
                      }}
                      type="button"
                      variant="outlined"
                      color='secondary'
                      onClick={ closeWindow }
                    >
                      { cancelTitle }
                    </ButtonComponent>
                  )
                }
                {
                  onSubmit && withSubmit && (
                    <ButtonComponent
                      sx={{
                        ml: 2,
                      }}
                      type="button"
                      variant="contained"
                      color="primary"
                      disabled={ disabled || loading ? true : false }
                      onClick={ onSubmit }
                    >
                      { submitTitle }
                    </ButtonComponent>
                  )
                }
                { rightButtons || buttons }
              </Box>
            </Box>
          </DialogActions>
        )
      }
    </Dialog>
  )
}

export default Window